import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMyBookingStore } from '../../stores/useMyBookingStore';
import RescheduleBookingDialog from '../../components/RescheduleBookingDialog';
import { toast } from 'react-hot-toast';

const MyBookingsPage: React.FC = () => {
  const { t } = useTranslation(['pages', 'translation']);
  const { storeId, token } = useParams<{ storeId: string; token: string }>();
  const navigate = useNavigate();

  const { bookings, isLoading, fetchBooking, rescheduleBooking, cancelBooking } = useMyBookingStore();
  const booking = token ? bookings[token] : null;

  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);

  useEffect(() => {
    if (token) {
      fetchBooking(token);
    }
  }, [token, fetchBooking]);

  const handleCancelBooking = () => {
    if (!booking || !token) return;

    // 立即關閉對話框（樂觀更新在 store 處理）
    setShowCancelDialog(false);
    cancelBooking(token).catch(() => {
      // 錯誤處理與 UI 回滾在 store 中完成
    });
  };

  const handleRescheduleBooking = async (newDateTime: string) => {
    if (!booking || !token) return;

    try {
      await rescheduleBooking(token, { date: newDateTime });
      setShowRescheduleDialog(false);
    } catch (error: any) {
      // Error already handled in store
      throw error;
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      PENDING: { label: t('translation:bookingStatus.PENDING'), color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' },
      CONFIRMED: { label: t('translation:bookingStatus.CONFIRMED'), color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
      CANCELLED: { label: t('translation:bookingStatus.CANCELLED'), color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' },
      COMPLETED: { label: t('translation:bookingStatus.COMPLETED'), color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.PENDING;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const timeStr = date.toLocaleTimeString('zh-TW', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `${dateStr} ${timeStr}`;
  };

  const canCancel = booking && (booking.status === 'PENDING' || booking.status === 'CONFIRMED');
  const canReschedule = booking && (booking.status === 'PENDING' || booking.status === 'CONFIRMED');

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">{t('pages:booking.myBookings.loading')}</p>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-12 text-center">
            <div className="text-6xl mb-4">❌</div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t('pages:booking.myBookings.notFound')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('pages:booking.myBookings.linkExpired')}
            </p>
            <button
              onClick={() => navigate(`/book/${storeId}`)}
              className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-bold transition-colors"
            >
              {t('pages:booking.myBookings.backToHome')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
      <div className="container mx-auto max-w-4xl">
        {/* 標題 */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('pages:booking.myBookings.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t('pages:booking.myBookings.subtitle')}
          </p>
        </div>

        {/* 預約詳情卡片 */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 mb-6">
          {/* 狀態標籤 */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                {t('pages:booking.myBookings.bookingDetails')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                {t('pages:booking.myBookings.bookingNumber')}{booking.id}
              </p>
            </div>
            {getStatusBadge(booking.status)}
          </div>

          {/* 預約資訊 */}
          <div className="space-y-4">
            {/* 服務項目 */}
            <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.myBookings.serviceItem')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {booking.service.name}
              </span>
            </div>

            {/* 預約時間 */}
            <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.myBookings.bookingTime')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {formatDateTime(booking.date)}
              </span>
            </div>

            {/* 服務時長 */}
            <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.myBookings.serviceDuration')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {booking.duration} {t('translation:common.time.minutes')}
              </span>
            </div>

            {/* 服務價格 */}
            <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.myBookings.servicePrice')}</span>
              <span className="font-semibold text-gray-900 dark:text-white text-xl">
                ${Math.floor(booking.price)}
              </span>
            </div>

            {/* 服務人員 */}
            {booking.staff && (
              <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-slate-700">
                <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.myBookings.staffMember')}</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {booking.staff.name}
                </span>
              </div>
            )}

            {/* 聯絡姓名 */}
            <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.myBookings.contactName')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {booking.customer.name}
              </span>
            </div>

            {/* 聯絡 Email */}
            <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.myBookings.contactEmail')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {booking.customer.email}
              </span>
            </div>

            {/* 聯絡電話 */}
            {booking.customer.phone && (
              <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-slate-700">
                <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.myBookings.contactPhone')}</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {booking.customer.phone}
                </span>
              </div>
            )}

            {/* 備註 */}
            {booking.note && (
              <div className="py-3">
                <span className="text-gray-600 dark:text-gray-400 block mb-2">{t('pages:booking.myBookings.note')}</span>
                <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-slate-700 p-3 rounded">
                  {booking.note}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* 操作按鈕 */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => navigate(`/book/${storeId}`)}
            className="flex-1 px-6 py-3 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg font-bold border-2 border-gray-300 dark:border-slate-600 hover:border-brand-500 dark:hover:border-brand-500 hover:text-brand-600 dark:hover:text-brand-400 transition-all duration-200"
          >
            {t('pages:booking.myBookings.backToHome')}
          </button>

          {canReschedule && (
            <button
              onClick={() => setShowRescheduleDialog(true)}
              className="flex-1 px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-bold transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              {t('pages:booking.myBookings.modifyBooking')}
            </button>
          )}

          {canCancel && (
            <button
              onClick={() => setShowCancelDialog(true)}
              className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              {t('pages:booking.myBookings.cancelBooking')}
            </button>
          )}
        </div>

        {/* 取消確認對話框 */}
        {showCancelDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                {t('pages:booking.myBookings.confirmCancel.title')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t('pages:booking.myBookings.confirmCancel.message')}
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={() => setShowCancelDialog(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors"
                >
                  {t('pages:booking.myBookings.confirmCancel.back')}
                </button>
                <button
                  onClick={handleCancelBooking}
                  className="flex-1 px-4 py-2 rounded-lg font-semibold text-white transition-colors bg-red-600 hover:bg-red-700"
                >
                  {t('pages:booking.myBookings.confirmCancel.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 修改預約對話框 */}
        {showRescheduleDialog && booking && (
          <RescheduleBookingDialog
            isOpen={showRescheduleDialog}
            onClose={() => setShowRescheduleDialog(false)}
            onSuccess={handleRescheduleBooking}
            booking={{
              id: booking.id,
              storeId: storeId!,
              serviceId: booking.service.id || '',
              staffId: booking.staff?.id,
              date: booking.date,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default MyBookingsPage;
